import React, { useState, useEffect } from "react";
import { IResourceComponentsProps } from "@pankod/refine-core";
import { 
  List, 
  Card, 
  Space, 
  Typography, 
  Table, 
  Tag, 
  Button, 
  Alert, 
  Empty, 
  Spin,
  Progress,
  Tooltip,
  Form,
  InputNumber,
  Checkbox
} from "@pankod/refine-antd";
import { 
  DollarCircleOutlined, 
  SyncOutlined, 
  SearchOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  FilterOutlined,
  PictureOutlined
} from "@ant-design/icons";
import { getSuppressedProducts, startPriceSync, getCleanupStatus } from "Services/productServices";
import toast from "react-hot-toast";

const { Text, Paragraph } = Typography;

export const PriceSync: React.FC<IResourceComponentsProps> = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [filters, setFilters] = useState({
    minPrice: 0,
    maxPrice: 10,
    noImage: false
  });

  const pollInterval = React.useRef<any>(null);

  const fetchSuppressed = async () => {
    setLoading(true);
    try {
      const response = await getSuppressedProducts(filters);
      if (response.data.success) {
        setProducts(response.data.products || []);
      } else {
        toast.error("Failed to fetch suppressed products");
      }
    } catch (error) {
      console.error("Fetch suppressed failed:", error);
      toast.error("Error connecting to bridge");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppressed();
  }, []);

  useEffect(() => {
    if (taskId) {
      pollInterval.current = setInterval(async () => {
        try {
          const statusRes = await getCleanupStatus(taskId);
          if (statusRes.data.success) {
            const { status, cleanedCount, totalToClean, error } = statusRes.data;
            setSyncProgress({ current: cleanedCount, total: totalToClean });
            
            if (status === 'completed') {
              clearInterval(pollInterval.current);
              setTaskId(null);
              setSyncing(false);
              toast.success(`Price sync complete! ${cleanedCount} products processed and activated.`);
              fetchSuppressed(); // Refresh list (synced items should disappear)
            } else if (status === 'failed') {
              clearInterval(pollInterval.current);
              setTaskId(null);
              setSyncing(false);
              toast.error(error || "Price sync task failed");
            }
          }
        } catch (err) {
          console.error("Sync polling error:", err);
        }
      }, 2000);
    }
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [taskId]);

  const handlePriceSync = async () => {
    if (selectedIds.length === 0) {
      toast.error("No products selected for price sync");
      return;
    }

    setSyncing(true);
    setSyncProgress({ current: 0, total: selectedIds.length });
    try {
      // Ensure IDs are strings for the API call
      const response = await startPriceSync(selectedIds.map(id => String(id)));
      if (response.data.success) {
        setTaskId(response.data.taskId);
        toast.success("Price sync task started in background...");
      } else {
        toast.error("Failed to start sync task");
        setSyncing(false);
      }
    } catch (error) {
      console.error("Price sync trigger failed:", error);
      toast.error("Error connecting to bridge");
      setSyncing(false);
    }
  };

  const columns = [
    {
      title: "SKU",
      dataIndex: "masterSku",
      key: "masterSku",
      render: (sku: string) => <Text strong>{sku}</Text>
    },
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Current CMS Price",
      dataIndex: "currentPrice",
      key: "currentPrice",
      render: (val: number) => `$${Number(val).toFixed(2)}`
    },
    {
      title: "Image",
      dataIndex: "hasImage",
      key: "hasImage",
      render: (has: boolean) => has ? <Tag color="success">YES</Tag> : <Tag color="error">NO IMAGE</Tag>
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, record: any) => (
        <Space>
          <Tag icon={<EyeInvisibleOutlined />} color="default">Hidden</Tag>
          {record.published ? <Tag color="blue">Published</Tag> : <Tag color="orange">Draft</Tag>}
        </Space>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys: React.Key[]) => setSelectedIds(keys),
  };

  return (
    <List 
      title="Safe Product Activation (Price Sync)"
      headerProps={{
        extra: (
          <Space>
            <Button 
              icon={<SyncOutlined />} 
              onClick={fetchSuppressed}
              loading={loading}
            >
              Scan Suppressed
            </Button>
            <Button 
              type="primary" 
              icon={<DollarCircleOutlined />} 
              onClick={handlePriceSync}
              loading={syncing}
              disabled={selectedIds.length === 0}
            >
              Sync Prices & Activate ({selectedIds.length})
            </Button>
          </Space>
        )
      }}
    >
      <Alert
        message="Onboarding Safeguard"
        description={
          <Paragraph>
            New products are now "Hidden-by-Default" (isSearchableProduct: OFF). Use these filters to identify 
            products ready for pricing and search activation.
          </Paragraph>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card title={<span><FilterOutlined /> Criteria Filters</span>} style={{ marginBottom: 24 }}>
        <Form layout="inline" onFinish={fetchSuppressed}>
          <Form.Item label={<span><DollarCircleOutlined /> Min Price</span>}>
            <InputNumber 
              value={filters.minPrice} 
              onChange={(val) => setFilters(f => ({ ...f, minPrice: Number(val) || 0 }))} 
              precision={2}
            />
          </Form.Item>
          <Form.Item label={<span><DollarCircleOutlined /> Max Price</span>}>
            <InputNumber 
              value={filters.maxPrice} 
              onChange={(val) => setFilters(f => ({ ...f, maxPrice: Number(val) || 0 }))} 
              precision={2}
            />
          </Form.Item>
          <Form.Item>
            <Checkbox 
              checked={filters.noImage} 
              onChange={(e) => setFilters(f => ({ ...f, noImage: e.target.checked }))}
            >
              <PictureOutlined /> Only Missing Images
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Button type="primary" icon={<SearchOutlined />} onClick={fetchSuppressed} loading={loading}>
              Apply Filters
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {syncing && (
        <Card style={{ marginBottom: 24, textAlign: 'center' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Syncing Pricing and Activating Products...</Text>
            <Progress 
              percent={Math.round((syncProgress.current / syncProgress.total) * 100)} 
              status="active" 
            />
            <Text type="secondary">{syncProgress.current} / {syncProgress.total} products completed</Text>
          </Space>
        </Card>
      )}

      <Card>
        <Table 
          dataSource={products} 
          columns={columns} 
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{ pageSize: 50 }}
        />
      </Card>
    </List>
  );
};
